<!-- 라우터에 매칭되지 않는 경로(404)에서 보여주는 페이지 -->
<script setup>
import { onMounted } from "vue";

// 이전 페이지로 복귀 (정본 404 디자인의 history.back 동작)
const goBack = () => window.history.back();

onMounted(() => {
  document.title = "페이지를 찾을 수 없습니다 · 당근영어 평생교육";
});
</script>

<template>
  <div class="nf-root">
    <!-- 사이트 히어로 톤의 부드러운 블롭 배경 -->
    <div class="bg" aria-hidden="true">
      <span class="blob b1"></span>
      <span class="blob b2"></span>
      <span class="blob b3"></span>
    </div>

    <main>
      <!-- 풍선 404 + Not Found 알약 묶음 -->
      <div class="hero">
        <img class="balloon" src="@/assets/images/404/img.png" alt="404" />
        <div class="pill"><span class="x">!</span> Not Found</div>
      </div>
      <h1>페이지를 찾을 수 없습니다</h1>
      <p class="desc">
        요청하신 페이지가 <b>존재하지 않거나 주소가 변경</b>되었어요.<br />
        아래 버튼을 눌러 이전 페이지로 돌아가 주세요.
      </p>
      <button class="btn" type="button" @click="goBack"><span class="arrow">←</span> 이전 페이지로 돌아가기</button>
    </main>

    <!-- 하단 로고 -->
    <footer>
      <img src="@/assets/images/404/logo.png" alt="당근영어 평생교육" />
    </footer>
  </div>
</template>

<style scoped>
.nf-root {
  --orange: #f26f21;
  --orange-deep: #e25c12;
  --lavender: #ede7ff;
  --lavender-2: #d9ccfa;
  --peach: #ffe3cc;
  --ink: #1f2330;
  --muted: #8a90a2;
  font-family: "Pretendard", "Noto Sans KR", "Malgun Gothic", "맑은 고딕", sans-serif;
  color: var(--ink);
  background: #fff;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}
.nf-root * {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* 사이트 히어로 톤의 부드러운 블롭 배경 */
.bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}
.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(8px);
  opacity: 0.55;
}
.blob.b1 {
  width: 520px;
  height: 520px;
  left: -160px;
  top: -180px;
  background: radial-gradient(circle at 30% 30%, var(--lavender-2), transparent 70%);
}
.blob.b2 {
  width: 460px;
  height: 460px;
  right: -150px;
  top: -120px;
  background: radial-gradient(circle at 60% 40%, var(--peach), transparent 70%);
}
.blob.b3 {
  width: 380px;
  height: 380px;
  right: 8%;
  bottom: -160px;
  background: radial-gradient(circle at 50% 50%, var(--lavender), transparent 72%);
}

main {
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 24px 32px;
}

/* 풍선 404 + Not Found 알약 묶음 */
.hero {
  position: relative;
  display: inline-block;
  margin-bottom: clamp(40px, 6vw, 72px);
}
/* 풍선 404 이미지 */
.balloon {
  display: block;
  width: clamp(280px, 46vw, 460px);
  height: auto;
  filter: drop-shadow(0 22px 26px rgba(213, 83, 12, 0.22));
}
/* Not Found 알약 배지 — 풍선 하단 가운데 틈에 안착 */
.pill {
  position: absolute;
  left: calc(67% - 1px);
  bottom: calc(-4% + 10px);
  transform: translateX(-50%);
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: 999px;
  padding: 8px 18px 8px 8px;
  box-shadow: 0 8px 22px rgba(31, 35, 48, 0.16);
  font-size: 15px;
  font-weight: 600;
  color: #3a3f4d;
  letter-spacing: 0.2px;
  white-space: nowrap;
}
.pill .x {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(145deg, #ff9a4d, var(--orange) 60%, var(--orange-deep));
  color: #fff;
  font-weight: 900;
  display: grid;
  place-items: center;
  font-size: 17px;
  line-height: 1;
  box-shadow: 0 3px 8px rgba(242, 111, 33, 0.45);
}

h1 {
  font-size: clamp(24px, 4.4vw, 34px);
  font-weight: 800;
  margin: 0 0 20px;
}
p.desc {
  font-size: clamp(15px, 2.4vw, 18px);
  color: var(--muted);
  line-height: 1.85;
  max-width: 520px;
}
p.desc b {
  color: #5a6072;
  font-weight: 700;
}

/* 이전 페이지로 돌아가기 버튼 */
.btn {
  margin-top: 48px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--orange);
  color: #fff;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  padding: 16px 30px;
  border-radius: 999px;
  box-shadow: 0 10px 22px rgba(242, 111, 33, 0.32);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}
.btn:hover {
  background: var(--orange-deep);
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(242, 111, 33, 0.4);
}
.btn:active {
  transform: translateY(0);
}
.btn .arrow {
  font-size: 19px;
  line-height: 1;
}

/* 하단 로고 — 맨 아래 부착 */
.nf-root footer {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  padding: 50px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid #f0f0f4;
  margin-top: 24px;
}
footer img {
  width: 75px;
  height: auto;
  opacity: 0.9;
}

@media (max-width: 480px) {
  main {
    padding-top: 48px;
  }
  /* 모바일에서만 Not Found 알약 10px 아래로 */
  .pill {
    bottom: calc(-4% + 0px);
  }
  /* 모바일 버튼: 글자폭에 딱 맞게 */
  .btn {
    font-size: 15px;
    padding: 13px 24px;
    gap: 8px;
  }
  .btn .arrow {
    font-size: 17px;
  }
  footer img {
    width: 50px;
  }
}
</style>
